import { DropdownWidth, typographyRem } from '@by-miles/bymiles-styles'
import { DateInputSplit, Dropdown, SegmentedControl } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { useContext, useEffect, useState } from 'react'

import { numbersToLimitOptions, yearsToTenOptions } from '../../../../constants'
import { genericDateErrorCopy } from '../../../../constants'
import useChatWidget from '../../../../hooks/useChatWidget'
import useDriverQueries from '../../../../hooks/useDriverQueries'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { ToolTip } from '../../../ToolTip'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { DateErrorState, DateState } from '../../utils/dateTypes'
import { YourTheir, YouThey, YouveTheyve } from '../../utils/driverCopyChange'
import { handleDropdownState } from '../../utils/handleDropdownState'
import { handlePrefilledDate } from '../../utils/handlePrefilledDate'
import { handlePrefilledDropdown } from '../../utils/handlePrefilledDropdown'
import { DriverPersonalInfoStyle } from './DriverPersonalInfoStyle'
const { h4: H4, h5: H5 } = typographyRem
dayjs.extend(utc)

export const DriverPersonalInfo = () => {
  const quote = useQuote()
  const { openChatWidget } = useChatWidget()
  const {
    state: { selectedDriverIndex, isMobile, showModal },
  } = useContext(ProviderContext)
  const { showErrorBanner, notAnsweredMessage, updateValidInputs, validInputs } = usePreQuoteContext()
  const { updatedDriverDetails, setUpdatedDriverDetails, updatedAdditionalDriver, setUpdatedAdditionalDriver } =
    useDriverPageContext()
  const { relationshipData } = useDriverQueries()

  const additionalDrivers = quote?.details?.additional_drivers || []
  const selectedDriver = selectedDriverIndex < 1 ? quote?.details?.proposer : additionalDrivers[selectedDriverIndex - 1]
  const [bornInUk, setBornInUk] = useState<boolean | undefined | null>(null)
  const [dateOfBirth, setDateOfBirth] = useState<DateState>(handlePrefilledDate(''))
  const [residencyDate, setResidencyDate] = useState<string>('')
  const [children, setChildren] = useState<number | undefined | null>(quote?.details?.proposer?.children)
  const [relationship, setRelationship] = useState<string>('')

  const [dateError, setDateError] = useState<boolean>(false)
  const [customDateError, setCustomDateError] = useState<string>('')

  const dobError = showErrorBanner && !validInputs[selectedDriverIndex]?.dob ? notAnsweredMessage : ''
  const bornInUkError = showErrorBanner && !validInputs[selectedDriverIndex]?.bornInUk ? notAnsweredMessage : ''
  const residencyError = showErrorBanner && !validInputs[selectedDriverIndex]?.residencyDate ? notAnsweredMessage : ''
  const childrenError = showErrorBanner && !validInputs[selectedDriverIndex]?.children ? notAnsweredMessage : ''
  const relationshipError = showErrorBanner && !validInputs[selectedDriverIndex]?.relationship ? notAnsweredMessage : ''

  useEffect(() => {
    setBornInUk(selectedDriver?.born_in_uk)
    setDateOfBirth(handlePrefilledDate(selectedDriver?.dob || ''))
    setResidencyDate(selectedDriver?.residency_date || '')
    setRelationship(additionalDrivers[selectedDriverIndex - 1]?.relationship || '')

    updateValidInputs({
      field: 'bornInUk',
      value: typeof selectedDriver?.born_in_uk === 'boolean',
      index: selectedDriverIndex,
    })
    updateValidInputs({
      field: 'residencyDate',
      value: selectedDriver?.born_in_uk === false ? false : true,
      index: selectedDriverIndex,
    })
    updateValidInputs({ field: 'dob', value: !!selectedDriver?.dob, index: selectedDriverIndex })
    if (selectedDriverIndex > 0) {
      updateValidInputs({
        field: 'relationship',
        value: additionalDrivers?.length > 0 && !!additionalDrivers[selectedDriverIndex - 1]?.relationship,
        index: selectedDriverIndex,
      })
    }
  }, [selectedDriverIndex])

  const handleLivedInUk = (value: boolean) => {
    DataLayer.customEvent({
      eventName: `born in uk ${value ? 'yes' : 'no'}`,
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    setBornInUk(value)
    setResidencyDate('')
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, bornInUk: value, residencyDate: '' })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, born_in_uk: value, residency_date: '' })
  }

  const handleCheckDate = () => {
    if (!dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year || dateOfBirth.year.toString()?.length < 4) {
      return
    } else if (dateError) {
      setCustomDateError(genericDateErrorCopy)
    } else {
      const { year, month, day } = dateOfBirth
      const dob = dayjs.utc(`${year}/${month}/${day}`).toISOString()
      !selectedDriverIndex
        ? setUpdatedDriverDetails({ ...updatedDriverDetails, dob })
        : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, dob })
    }
  }

  const handleDobErrors = (errors: DateErrorState) => {
    if (errors.day || errors.month || errors.year) {
      setDateError(true)
    }
  }

  const handleResidencyDate = (years: number) => {
    DataLayer.customEvent({
      eventName: 'residency date',
      section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
      eventType: 'form tracking',
    })
    const date = dayjs().subtract(years, 'y').toISOString()
    setResidencyDate(date)
    !selectedDriverIndex
      ? setUpdatedDriverDetails({ ...updatedDriverDetails, residencyDate: date })
      : setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, residency_date: date })
  }

  const handlePreviousResidencyDate = (date: string) => {
    if (!date) return undefined
    const yearsFromNow = dayjs().diff(date, 'year')
    if (yearsFromNow >= 10) {
      return '10 years'
    } else if (yearsFromNow < 1) {
      return 'none'
    } else {
      return `${yearsFromNow} year${yearsFromNow > 1 ? 's' : ''}`
    }
  }

  useEffect(() => {
    if (selectedDriverIndex) return
    if ((updatedDriverDetails?.dob && bornInUk) || (updatedDriverDetails?.bornInUk && validInputs[0].dob)) {
      setUpdatedDriverDetails({
        ...updatedDriverDetails,
        residencyDate: updatedDriverDetails?.dob || quote?.details?.proposer?.dob,
      })
    }
  }, [updatedDriverDetails?.dob, bornInUk])

  useEffect(() => {
    if (!selectedDriverIndex) return
    if (
      (updatedAdditionalDriver?.dob && updatedAdditionalDriver?.born_in_uk) ||
      (updatedAdditionalDriver?.born_in_uk && validInputs[selectedDriverIndex].dob)
    ) {
      setUpdatedAdditionalDriver({
        ...updatedAdditionalDriver,
        residency_date: updatedAdditionalDriver?.dob,
      })
    }
  }, [updatedAdditionalDriver?.dob, bornInUk])

  useEffect(() => {
    const hasError =
      !dateOfBirth?.day || !dateOfBirth?.month || !dateOfBirth?.year || dateOfBirth.year.toString()?.length < 4
    const validDob = Object.keys(dateOfBirth)?.length === 3 && !hasError && !dateError
    updateValidInputs({ field: 'dob', value: validDob, index: selectedDriverIndex })
  }, [dateOfBirth, dateError])
  useEffect(() => {
    updateValidInputs({ field: 'bornInUk', value: typeof bornInUk === 'boolean', index: selectedDriverIndex })
    updateValidInputs({
      field: 'residencyDate',
      value: typeof bornInUk === 'boolean' ? bornInUk : true,
      index: selectedDriverIndex,
    })
  }, [bornInUk])
  useEffect(() => {
    updateValidInputs({
      field: 'residencyDate',
      value: bornInUk || bornInUk === null ? true : !!residencyDate,
      index: selectedDriverIndex,
    })
  }, [residencyDate])
  useEffect(() => {
    updateValidInputs({ field: 'children', value: typeof children === 'number', index: selectedDriverIndex })
  }, [children])
  useEffect(() => {
    selectedDriverIndex &&
      updateValidInputs({ field: 'relationship', value: !!relationship, index: selectedDriverIndex })
  }, [relationship])

  return (
    <DriverPersonalInfoStyle>
      <GenericSectionStyle>
        <H4 className="title">{`${YourTheir()} personal information.`}</H4>
        <div className="generic-wrapper">
          <div className="question-text-container" id="dob">
            <H5>{`When were ${YouThey()} born?`}</H5>
          </div>
          <div className="input-info-container">
            <div className="date-input">
              <DateInputSplit
                dataCy="dob-text-field"
                showLabels={true}
                validateOnBlur={false}
                toggleErrorMessage={!!customDateError || !!dobError}
                onBlur={() => handleCheckDate()}
                errorMessage={customDateError || dobError || undefined}
                onError={handleDobErrors}
                onChange={(value: number, field: string) => {
                  setCustomDateError('')
                  setDateError(false)
                  setDateOfBirth({
                    ...dateOfBirth,
                    [field]: value,
                  })
                  DataLayer.customEvent({
                    eventName: `dob ${field}`,
                    section: `${selectedDriverIndex ? 'additional ' : ''}driver`,
                    eventType: 'form tracking',
                  })
                }}
                defaultValue={dateOfBirth}
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
            </div>
            <ToolTip
              text={`The way people drive at different ages can influence how likely they might be to make a claim, so we need to know this to help work out the cost of ${YourTheir()} premium.`}
              className="tool-tip tip-top-small tool-align-center"
              dataCy="dob-info-button"
              tabFocusTrap={showModal ? -1 : 0}
            />
          </div>
          <fieldset className="question-container" id="bornInUk">
            <legend className="question-text-container">
              <H5>{`Have ${YouThey()} lived in the UK continuously since the age of 17?`}</H5>
            </legend>
            <div className="align-center input-info-container">
              <SegmentedControl
                options={[
                  { label: 'yes', value: true },
                  { label: 'no', value: false },
                ]}
                name="uk-born"
                preFilledValue={bornInUk}
                error={bornInUkError}
                onClick={value => typeof value === 'boolean' && handleLivedInUk(value)}
                dataCy="uk-born-toggle"
                tabFocusTrap={showModal ? -1 : 0}
                ariaLiveOnFocus
              />
              <ToolTip
                text={`This helps us work out how much experience ${YouThey()} have of driving (and being driven) on roads in Great Britain,
              Northern Ireland and/or the Isle of Man. If ${YouveTheyve()} lived outside of these areas for more than 12 months in a
              row, please answer 'No'.`}
                className="tool-tip tip-top-medium"
                dataCy="born-in-uk-info-button"
                tabFocusTrap={showModal ? -1 : 0}
              />
            </div>
          </fieldset>
          {bornInUk === false && (
            <div className="question-container" id="residencyDate">
              <div className="question-text-container">
                <H5>{`How many of the last 10 years have ${YouThey()} spent living in the UK?`}</H5>
              </div>
              <div className="input-info-container">
                <Dropdown
                  handleDropdownChange={option => handleResidencyDate(option.value)}
                  isMobile={isMobile}
                  options={yearsToTenOptions}
                  preFilled={handlePreviousResidencyDate(residencyDate)}
                  states={handleDropdownState(residencyDate, residencyError)}
                  errorMessage={residencyError}
                  width={DropdownWidth.Large}
                  className={`dropdown-small-with-icon ${selectedDriverIndex ? 'dropdown-top-heaven' : 'dropdown'}`}
                  placeholder="Select"
                  dataCy="years-in-uk-list"
                  tabFocusTrap={showModal ? -1 : 0}
                  ariaLiveOnFocus
                />
                <ToolTip
                  text={
                    <div>
                      <span>
                        {`This should be the total number of full years ${YouveTheyve()} spent in UK over the last 10 years. If you're not sure what to answer, then get in touch with us `}
                      </span>
                      <span className="modal-link" onClick={() => openChatWidget()}>
                        {'here'}
                      </span>
                      <span>{'.'}</span>
                    </div>
                  }
                  className="tool-tip tip-top-medium"
                  tabFocusTrap={showModal ? -1 : 0}
                />
              </div>
            </div>
          )}
          {!selectedDriverIndex ? (
            <div className="question-container" id="children">
              <div className="question-text-container">
                <H5>How many children under 16 do you have?</H5>
              </div>
              <div className="dropdown-wrapper" id="driver-title">
                <Dropdown
                  handleDropdownChange={(option: any) => {
                    setChildren(option.value)
                    DataLayer.customEvent({
                      eventName: 'number of children',
                      section: 'driver',
                      eventType: 'form tracking',
                    })
                    setUpdatedDriverDetails({ ...updatedDriverDetails, children: option.value })
                  }}
                  isMobile={isMobile}
                  options={numbersToLimitOptions(5)}
                  preFilled={handlePrefilledDropdown(numbersToLimitOptions(5), children)}
                  states={handleDropdownState(children, childrenError)}
                  errorMessage={childrenError}
                  width={DropdownWidth.Large}
                  className="dropdown-small dropdown-bellow"
                  placeholder="Select"
                  dataCy="children-under-16-dropdown-list"
                  tabFocusTrap={showModal ? -1 : 0}
                  ariaLiveOnFocus
                />
              </div>
            </div>
          ) : (
            <>
              {relationshipData && (
                <div className="question-container" id="relationship">
                  <div className="question-text-container">
                    <H5>What is their relationship to the main driver?</H5>
                  </div>
                  <div className="dropdown-wrapper" id="driver-title">
                    <Dropdown
                      handleDropdownChange={option => {
                        setRelationship(option.value)
                        DataLayer.customEvent({
                          eventName: 'relationship',
                          section: 'additional driver',
                          eventType: 'form tracking',
                        })
                        setUpdatedAdditionalDriver({ ...updatedAdditionalDriver, relationship: option.value })
                      }}
                      isMobile={isMobile}
                      options={relationshipData?.relationships || []}
                      preFilled={handlePrefilledDropdown(relationshipData?.relationships || [], relationship)}
                      states={handleDropdownState(relationship, relationshipError)}
                      errorMessage={relationshipError}
                      width={DropdownWidth.Large}
                      className="dropdown-small dropdown-top-top"
                      placeholder="Select"
                      dataCy="relationship"
                      tabFocusTrap={showModal ? -1 : 0}
                      ariaLiveOnFocus
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </GenericSectionStyle>
    </DriverPersonalInfoStyle>
  )
}
